import { SearchOutlined } from "@ant-design/icons";
import type { SelectProps } from "antd";
import clsx from "clsx";
import type { ReactNode } from "react";
import "./AppTableToolbar.scss";
import { AppButton, type AppButtonProps } from "./AppButton";
import { AppInput, type AppInputProps } from "./AppInput";
import { AppSelect, type AppSelectProps } from "./AppSelect";

type ToolbarValue = string | number;

type AppTableToolbarFilterManagedProps =
  | "key"
  | "value"
  | "options"
  | "onChange"
  | "icon"
  | "width"
  | "className";

export type AppTableToolbarSearch = {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  prefix?: ReactNode;
  width?: number | string;
  inputProps?: Omit<
    AppInputProps,
    "value" | "placeholder" | "onChange" | "onSearch" | "prefix"
  >;
};

export type AppTableToolbarFilter = {
  key: string;
  value: ToolbarValue;
  options: SelectProps["options"];
  onChange: (value: ToolbarValue | undefined) => void;
  icon?: ReactNode;
  width?: number | string;
  className?: string;
} & Omit<
  AppSelectProps<ToolbarValue>,
  AppTableToolbarFilterManagedProps | "value" | "options" | "onChange" | "className"
>;

type AppTableToolbarPageSizeManagedProps =
  | "value"
  | "options"
  | "onChange"
  | "width";

export type AppTableToolbarPageSize = {
  value: number;
  options?: number[];
  onChange: (value: number) => void;
  width?: number | string;
} & Omit<
  AppSelectProps<number>,
  AppTableToolbarPageSizeManagedProps | "value" | "options" | "onChange"
>;

export type AppTableToolbarViewOption = {
  key: string;
  icon: ReactNode;
  label: string;
};

export type AppTableToolbarViewMode = {
  value: string;
  options: AppTableToolbarViewOption[];
  onChange: (value: string) => void;
};

export type AppTableToolbarProps = {
  className?: string;
  search?: AppTableToolbarSearch;
  filterLabel?: ReactNode;
  filters?: AppTableToolbarFilter[];
  pageSize?: AppTableToolbarPageSize;
  viewMode?: AppTableToolbarViewMode;
  leftActions?: ReactNode;
  actions?: ReactNode;
};

const getWidthStyle = (width?: number | string) => {
  if (!width) {
    return undefined;
  }

  return { width: typeof width === "number" ? `${width}px` : width };
};

const normalizeSelectValue = (value: ToolbarValue) => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  return value;
};

export const AppTableToolbar = ({
  className,
  search,
  filterLabel = "Filter By:",
  filters = [],
  pageSize,
  viewMode,
  leftActions,
  actions,
}: AppTableToolbarProps) => {
  const hasControls = filters.length > 0 || pageSize;

  return (
    <div className={clsx("app-ant-table-toolbar", className)}>
      <div className="app-ant-table-toolbar-left">
        {search && (
          <div
            className="app-ant-table-toolbar-search"
            style={getWidthStyle(search.width)}
          >
            <AppInput
              value={search.value}
              onChange={(event) => search.onChange(event.target.value)}
              onSearch={search.onSearch}
              prefix={search.prefix ?? <SearchOutlined />}
              placeholder={search.placeholder}
              {...search.inputProps}
            />
          </div>
        )}
        {leftActions}
      </div>

      {hasControls && (
        <div className="app-ant-table-toolbar-controls">
          {filterLabel && (
            <span className="app-ant-table-toolbar-label">{filterLabel}</span>
          )}
          {filters.map((filter) => {
            const {
              key,
              value,
              options,
              onChange,
              icon,
              width,
              className: filterClassName,
              ...selectProps
            } = filter;

            return (
            <div
              key={key}
              className={clsx(
                "app-ant-table-toolbar-filter",
                icon && "app-ant-table-toolbar-filter-has-icon",
                filterClassName,
              )}
              style={getWidthStyle(width ?? 133)}
            >
              {icon && (
                <span className="app-ant-table-toolbar-control-icon">
                  {icon}
                </span>
              )}
              <AppSelect<ToolbarValue>
                className={clsx(
                  "app-ant-table-toolbar-select",
                  icon && "app-ant-table-toolbar-select-with-icon",
                )}
                value={normalizeSelectValue(value)}
                options={options}
                onChange={onChange}
                {...selectProps}
              />
            </div>
            );
          })}
          {pageSize && (
            (() => {
              const {
                value,
                options,
                onChange,
                width,
                searchable = false,
                ...selectProps
              } = pageSize;

              return (
            <div
              className="app-ant-table-toolbar-page-size"
              style={getWidthStyle(width)}
            >
              <AppSelect<number>
                value={value}
                options={(options ?? [5, 8, 10, 15, 20]).map((item) => ({
                  value: item,
                  label: item,
                }))}
                onChange={onChange}
                searchable={searchable}
                {...selectProps}
              />
            </div>
              );
            })()
          )}
        </div>
      )}

      {(viewMode || actions) && (
        <div className="app-ant-table-toolbar-actions">
          {viewMode && (
            <div className="app-ant-table-toolbar-view-mode">
              {viewMode.options.map((option) => (
                <AppButton
                  key={option.key}
                  tone={viewMode.value === option.key ? "primary" : "light"}
                  shape="circle"
                  icon={option.icon}
                  aria-label={option.label}
                  title={option.label}
                  onClick={() => viewMode.onChange(option.key)}
                />
              ))}
            </div>
          )}
          {actions}
        </div>
      )}
    </div>
  );
};

export type AppTableToolbarActionButtonProps = AppButtonProps;
